import { STORAGE_KEY_PREFIX } from './onboardingConfig.js';

/**
 * @typedef {{ status: 'completed' | 'skipped' | 'pending', completedAt?: string }} TourProgressEntry
 * @typedef {Record<string, TourProgressEntry>} TourProgressMap
 */

/**
 * @param {string} uid
 * @returns {string}
 */
function localKey(uid) {
  return `${STORAGE_KEY_PREFIX}${uid}`;
}

/**
 * @param {string} uid
 * @returns {TourProgressMap | null}
 */
export function readLocalTourProgress(uid) {
  if (!uid || typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(localKey(uid));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * @param {string} uid
 * @param {TourProgressMap} progress
 */
export function writeLocalTourProgress(uid, progress) {
  if (!uid || typeof window === 'undefined') return;
  try {
    localStorage.setItem(localKey(uid), JSON.stringify(progress));
  } catch (e) {
    console.warn('[onboarding] localStorage write failed', e);
  }
}

/**
 * Merge Firestore profile progress with local cache (local wins on conflicts).
 * @param {string} uid
 * @param {TourProgressMap | null | undefined} profileProgress
 * @returns {TourProgressMap}
 */
export function mergeTourProgress(uid, profileProgress) {
  const local = readLocalTourProgress(uid) || {};
  const remote = profileProgress && typeof profileProgress === 'object' ? profileProgress : {};
  const merged = { ...remote, ...local };
  if (uid) writeLocalTourProgress(uid, merged);
  return merged;
}

/**
 * @param {TourProgressMap | null | undefined} progress
 * @param {string} tourId
 * @returns {boolean}
 */
export function isTourDone(progress, tourId) {
  const status = progress?.[tourId]?.status;
  return status === 'completed' || status === 'skipped';
}

/**
 * @param {TourProgressMap | null | undefined} progress
 * @param {string} tourId
 * @returns {'completed' | 'skipped' | 'pending'}
 */
export function getTourStatus(progress, tourId) {
  const status = progress?.[tourId]?.status;
  if (status === 'completed' || status === 'skipped') return status;
  return 'pending';
}

/**
 * @param {string} uid
 * @param {string} tourId
 * @param {'completed' | 'skipped'} status
 * @param {TourProgressMap} currentProgress
 * @param {(data: object) => Promise<void> | void} [updateUserProfile]
 * @returns {Promise<TourProgressMap>}
 */
export async function persistTourStatus(uid, tourId, status, currentProgress, updateUserProfile) {
  const now = new Date().toISOString();
  const next = {
    ...(currentProgress || {}),
    [tourId]: { status, completedAt: now },
  };
  writeLocalTourProgress(uid, next);
  if (updateUserProfile) {
    try {
      await updateUserProfile({ tourProgress: next });
    } catch (e) {
      console.warn('[onboarding] Firestore sync failed', e);
    }
  }
  return next;
}

/**
 * @param {string} uid
 * @param {string} tourId
 * @param {TourProgressMap} currentProgress
 * @param {(data: object) => Promise<void> | void} [updateUserProfile]
 * @returns {Promise<TourProgressMap>}
 */
export async function resetTourProgress(uid, tourId, currentProgress, updateUserProfile) {
  const next = { ...(currentProgress || {}) };
  delete next[tourId];
  writeLocalTourProgress(uid, next);
  if (updateUserProfile) {
    try {
      await updateUserProfile({ tourProgress: next });
    } catch (e) {
      console.warn('[onboarding] Firestore reset sync failed', e);
    }
  }
  return next;
}
